import React, { useEffect, useState, useCallback } from "react";
import { Skeleton, ErrorState } from "../ui/LoadingStates";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const STATUS_STYLE = {
  present: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  absent:  "text-red-400 bg-red-500/10 border-red-500/20",
  leave:   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  wfh:     "text-blue-400 bg-blue-500/10 border-blue-500/20",
  late:    "text-orange-400 bg-orange-500/10 border-orange-500/20",
};

const fmt = {
  date: d => { if (!d) return "—"; const dt = new Date(d); return isNaN(dt) ? String(d) : dt.toISOString().split("T")[0]; },
  time: t => { if (!t) return "—"; const dt = new Date(t); return isNaN(dt) ? String(t) : dt.toTimeString().slice(0, 5); },
};

export default function AttendanceTable() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [page, setPage]     = useState(1);
  const PER_PAGE = 10;

  const load = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    setLoading(true); setError(null);
    fetch(`${API_BASE}/api/attendance/history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(rows.length / PER_PAGE);
  const visible    = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] flex flex-col">
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Attendance Log</span>
        {!loading && !error && (
          <span className="text-xs text-white/30">{rows.length} records</span>
        )}
      </div>

      {loading && (
        <div className="p-5" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <Skeleton width="20%" height={10} />
              <Skeleton width="15%" height={18} radius={999} />
              <Skeleton width="15%" height={10} />
              <Skeleton width="15%" height={10} />
              <Skeleton width="25%" height={10} />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="p-5">
          <ErrorState message="Couldn't load attendance log" detail={error} onRetry={load} />
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="w-full">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  {["Date", "Status", "In", "Out", "Notes"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.length > 0 ? visible.map((r, i) => {
                  const statusKey = String(r.status || "").toLowerCase();
                  const statusStyle = STATUS_STYLE[statusKey] || "text-white/40 bg-white/5 border-white/10";
                  return (
                    <tr key={r.id ?? i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                      <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmt.date(r.date)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full border capitalize ${statusStyle}`}>
                          {r.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/50 font-mono text-xs whitespace-nowrap">{fmt.time(r.check_in)}</td>
                      <td className="px-4 py-3 text-white/50 font-mono text-xs whitespace-nowrap">{fmt.time(r.check_out)}</td>
                      <td className="px-4 py-3 text-white/40 text-xs truncate max-w-[120px]">{r.notes || "—"}</td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-white/20 text-sm italic">
                      No attendance records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 text-[#00ff90]/50 hover:text-[#00ff90] hover:border-[#00ff90]/40 disabled:opacity-20 transition-colors"
              >← Prev</button>
              <span className="text-xs text-white/30">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 text-[#00ff90]/50 hover:text-[#00ff90] hover:border-[#00ff90]/40 disabled:opacity-20 transition-colors"
              >Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}